import { ApolloClient, DocumentNode } from '@apollo/client';
import { captureException as captureSentryException } from '@sentry/react';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { error as commonError } from '@finley-technologies/common';

const isSuccessStatus = (status: number) => status >= 200 && status < 300;

// the function below is split in 3 stages to allow for better error handling and logging
type OperationStage = 'fetchUrl' | 'parseResponse' | 'uploadFile';

/**
 * POST `file` to a presigned S3 url with its signed form fields (S3 requires
 * them before the file). Throws unless S3 answers 2xx.
 */
export const postFileToPresignedUrl = async ({
  url,
  fields,
  file,
  progressEventCallback,
}: {
  url: string;
  fields: ReadonlyArray<{ name: string; value: string }>;
  file: File;
  progressEventCallback?: (progressEvent: ProgressEvent) => void;
}): Promise<AxiosResponse> => {
  const formData = new FormData();
  fields.forEach(({ name, value }) => {
    formData.append(name, value);
  });
  formData.append('file', file);

  const axiosConfig: AxiosRequestConfig = {
    onUploadProgress: (progressEvent: ProgressEvent) => {
      if (progressEventCallback) progressEventCallback(progressEvent);
    },
  };

  const s3Response = await axios.post(url, formData, axiosConfig);
  if (!isSuccessStatus(s3Response.status)) {
    throw new Error(
      `S3 responded ${s3Response.status}: ${s3Response.statusText}`,
    );
  }
  return s3Response;
};

/**
 * This is intended to be used for file uploads direct to S3 from the client
 *
 * Usage instructions:
 * (1) Add imports
 *      import { useApolloClient } from '@apollo/client';
 *      import { presignedPostUpload } from 'packages/files/ui/utils';
 *      import { QUERY_URL_GQL_STRING } from 'packages/files/requests/files.gql';
 * (2) Initialize apollo client in React component
 *      const apolloClient = useApolloClient();
 * (3) Call async function with relevant parameters for endpoint
 *      await presignedPostUpload<{queryVariables}>({
 *        client: apolloClient,
 *        query: QUERY_URL_GQL_STRING,
 *        queryVariables,
 *      });
 *
 * Progress event callback instructions:
 * To utilize progress event callback, destructure attributes below to calculate status
 * Example:
 *    const { loaded, total } = progressEvent;
 *    const percentCompleted = Math.round((loaded * 100) / total);
 *
 * @param client Initialize a client with useApolloClient() from '@apollo/client'
 * @param file
 * @returns axios Response
 */
export const presignedPostUpload = async <QueryRequest, QueryResponse>({
  client,
  file,
  query,
  queryVariables,
  progressEventCallback,
}: {
  client: ApolloClient<object>;
  file: File;
  query: DocumentNode;
  queryVariables: QueryRequest;
  progressEventCallback?: (progressEvent: ProgressEvent) => any;
}): Promise<{
  s3Response: AxiosResponse;
  s3FilePath: string;
  queryResponse: QueryResponse;
}> => {
  let stage: OperationStage = 'fetchUrl';

  try {
    const serializedParams = JSON.stringify(queryVariables);

    const { data } = await client.query({
      fetchPolicy: 'network-only',
      query,
      variables: {
        serializedParams,
      },
    });

    stage = 'parseResponse';
    const url = data.getSignedUploadUrl.url;
    const s3RequestFields: { [key: string]: string } = JSON.parse(
      data.getSignedUploadUrl.s3RequestFields,
    );

    let queryResponse = {} as QueryResponse;
    if (data.getSignedUploadUrl?.queryResponseFields) {
      queryResponse = JSON.parse(data.getSignedUploadUrl.queryResponseFields);
    }

    stage = 'uploadFile';
    const s3Response = await postFileToPresignedUrl({
      url,
      fields: Object.entries(s3RequestFields).map(([name, value]) => ({
        name,
        value,
      })),
      file,
      progressEventCallback,
    });

    return { s3Response, s3FilePath: s3RequestFields.key, queryResponse };
  } catch (error) {
    const errorMessage = commonError.getErrorMessage(error);
    const outerError = new Error(
      `Failed to upload file to S3: ${stage} : ${errorMessage}. queryVariables: [${JSON.stringify(
        queryVariables,
      )}]`,
    );

    captureSentryException(outerError);
    throw outerError;
  }
};
